import {
  getLanguages,
  getQuestionnaires,
  getCompanies,
} from "../../repository/data.repository";
import { retry } from "../../utils/http";
import { nameOf } from "../../utils/typescript";
import { QueryOperation } from "../../code/search-query/search-query";
import {
  Questionnaire,
  Bundle,
  Language,
  Company,
} from "../../repository/data.default";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import DynamicFormActions from "../../controls/DynamicFormActions";

export interface BundlesFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: Bundle;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function BundlesFormDialog(props: BundlesFormDialogProps) {
  // Get schema based on mode
  const getSchema = (): DynamicSchema => {
    return {
      url: "/api/Data/Bundles",
      idField: "uid",
      supportedActions: ["add", "update", "delete"],
      relatedEntitiesUI: {
        languages: {
          title: "Languages",
          getUrl: "/api/Data/Bundles/{id}/Languages",
          deleteUrl: "/api/Data/Bundles/{id}/RemoveLanguage/{value}",
          addUrl: "/api/Data/Bundles/{id}/AssignLanguage/{value}",
          addLabel: "Add Language",
          emptyText: "No languages added",
          entityConfiguration: {
            listNameToDisplay: "name",
            UidName: "uid",
          },
          request: languagesRequest,
        },
        questionnaires: {
          needsOrderToAdd: true,
          title: "Questionnaires",
          addLabel: "Add Questionnaire",
          getUrl: "/api/Data/Bundles/{id}/Questionnaires",
          deleteUrl: "/api/Data/Bundles/{id}/RemoveQuestionnaire/{value}",
          addUrl: "/api/Data/Bundles/{id}/AssignQuestionnaire/{value}",
          emptyText: "No questionnaires added",
          entityConfiguration: {
            listNameToDisplay: "questionnaireName",
            UidName: "questionnaireUid",
          },
          request: questionnairesRequest,
        },
        companies: {
          title: "Companies",
          addLabel: "Add Company",
          getUrl: "/api/Data/Bundles/{id}/Companies",
          deleteUrl: "/api/Data/Bundles/{id}/RemoveCompany/{value}",
          addUrl: "/api/Data/Bundles/{id}/AssignCompany/{value}",
          emptyText: "No companies added",
          entityConfiguration: {
            listNameToDisplay: "companyName",
            UidName: "companyUid",
          },
          request: companiesRequest,
        },
      },
      groups: [
        {
          controls: [
            {
              type: "textbox",
              name: "name",
              label: "Name",
              rules: [
                {
                  required: true,
                  message: "Name is required",
                },
              ],
            },
            {
              type: "areabox",
              name: "description",
              label: "Description",
              rules: [
                {
                  required: true,
                  message: "Description is required",
                },
              ],
              rows: 3,
            },
          ],
        },
      ],
    };
  };

  // Languages request for combobox
  const languagesRequest = (search: string) => {
    return retry(() =>
      getLanguages({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Language>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  // Questionnaires request for combobox — only a PUBLISHED version can be delivered (the server refuses the rest);
  // publishing a newer version moves the bundle onto it.
  const questionnairesRequest = (search: string) => {
    return retry(() =>
      getQuestionnaires({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Questionnaire>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<Questionnaire>("status"),
              operation: QueryOperation.Equal,
              values: ["Published"],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.version ? `${dataItem.name} (v${dataItem.version})` : dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  // Companies request for combobox
  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  // Get button text based on mode
  const getButtonText = () => {
    switch (props.mode) {
      case "add":
        return "Add";
      case "update":
        return "Save";
      case "delete":
        return "Delete";
      default:
        return "Submit";
    }
  };

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "large"}
      title={modalTitle(props.mode, "Bundle", props.initialValues?.name)}
      onCancel={props.onCancel}
      actions={null}
    >
      <DynamicFormActions
        mode={props.mode}
        initialValues={props.initialValues}
        onFinish={props.onFinish}
        onCancel={props.onCancel}
        schema={getSchema()}
        className="w-auto"
        confirmButtonText={getButtonText()}
        errorText="Something went wrong, try again"
      />
    </InModal>
  );
}

export default BundlesFormDialog;
